import { globalThemeManager } from 'foundations-theming/utils';
import { COLOR_OVERRIDE_CONFIGS, FONT_OVERRIDE_CONFIGS, BORDER_RADIUS_OVERRIDE_CONFIGS, deriveGenericColorVariants, deriveSurfaceTokensFromStyle, SURFACE_STYLE_OVERRIDE_CONFIG, buildFontFamilyString, resolveThemeFontFamily, applyThinFontWeightFloor } from './registry';
import { getIsThemePreview, getPortalAccountId, getPortalInstanceId, getPortalName } from './metadata';
import { FETCH_BRAND_DATA } from '../context/PortalDataContext';
import { Metrics } from './metrics';
import { trackPortalInitialized } from './usageTracker';
import { LOGIN_VERIFY_QUERY } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc.dfc.new';

// Theme category prefixes for parsing trellis-prefixed keys
const THEME_CATEGORY_PREFIXES = {
  Color: 'color',
  BorderRadius: 'borderRadius',
  BorderWidth: 'borderWidth',
  BoxShadow: 'boxShadow',
  Space: 'space',
  Typography: 'typography'
};
const defaultThemeOverrides = {
  color: {
    // Neutral/Grayscale colors for UI chrome
    'fill-primary-default': '#121212',
    // dark charcoal gray
    'fill-secondary-default': '#5C5C5C',
    // medium gray
    'fill-tertiary-default': '#8A8A8A',
    // light gray
    // Interactive text color
    'text-interactive-default': '#666666',
    // medium gray
    // Semantic status colors
    'fill-accent-green-default': '#2E7D32',
    // success/positive (dark green)
    'fill-accent-red-default': '#C62828',
    // error/alert (dark red)
    'fill-caution-default': '#ED9C28',
    // warning/caution (orange)
    'fill-info-default': '#0277BD',
    // info (deep blue)
    // Accent colors for Badge (free, beta, count, dot variants)
    'fill-accent-orange-default': '#E65100',
    'fill-accent-purple-default': '#6A1B9A'
  }
};

// Remove the foundations Table's header divider by zeroing its width token.
// Trellis sets this token to 0px in light and 1px in dark, so it only has an
// effect in dark mode. There it removes both the vertical dividers between
// header cells and the table's outer perimeter — foundations draws both from
// this one width token.
//
// We zero the width rather than hide the divider with a transparent color: under
// border-collapse, a color-hidden divider still leaves a notch where it crosses
// the header→body line.
//
// The cast is needed because fwc-* tokens aren't in the base BorderWidth union.
const defaultBorderWidthOverrides = {
  'fwc-table-header-divider-width-default': '0'
};

// Apply derivation functions to default overrides to generate all color tokens.
// This ensures the default theme has all current color tokens we override even without brand/theme settings.
function applyDefaultOverrides(surfaceStyle = 'light') {
  const colorOverrides = {};

  // Derive all variants for each default base color
  Object.entries(COLOR_OVERRIDE_CONFIGS).forEach(([tokenName, {
    deriveVariants
  }]) => {
    var _defaultThemeOverride;
    const baseColor = (_defaultThemeOverride = defaultThemeOverrides.color) === null || _defaultThemeOverride === void 0 ? void 0 : _defaultThemeOverride[tokenName];
    if (baseColor) {
      Object.assign(colorOverrides, deriveVariants(baseColor));
    }
  });

  // Derive surface color variants matching the effective style. Only tokens
  // where portals intentionally diverges from the Trellis base are returned;
  // all other surface tokens come from the Trellis light or dark base theme.
  Object.assign(colorOverrides, SURFACE_STYLE_OVERRIDE_CONFIG.deriveVariants(surfaceStyle));
  return {
    color: colorOverrides,
    borderWidth: defaultBorderWidthOverrides
  };
}
function isFontValue(value) {
  return value !== null && value !== undefined && typeof value === 'object' && 'font' in value && typeof value.font === 'string';
}
function isFontFieldValue(value) {
  return value !== null && value !== undefined && typeof value === 'object' && 'font' in value && typeof value.font === 'string';
}
export function deepMerge(target, source) {
  const result = Object.assign({}, target);
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];
      if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue) && targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
        result[key] = deepMerge(targetValue, sourceValue);
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue;
      }
    }
  }
  return result;
}
function camelToKebab(str) {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
}

// Expected format: `trellis${themeCategory}${tokenName}`
function parseThemeSettingKey(key) {
  // TODO: fix this for font family
  if (key === 'fontFamily') {
    return {
      category: 'typography',
      tokenName: 'fontFamily'
    };
  }

  // Handle trellis-prefixed keys
  if (key.startsWith('trellis')) {
    // Remove 'trellis' prefix
    const tokenKeyWithoutPrefix = key.replace(/^trellis/, '');

    // Find matching category prefix
    for (const [prefix, category] of Object.entries(THEME_CATEGORY_PREFIXES)) {
      if (tokenKeyWithoutPrefix.startsWith(prefix)) {
        const tokenName = camelToKebab(tokenKeyWithoutPrefix.replace(new RegExp(`^${prefix}`), ''));
        return {
          category: category,
          tokenName
        };
      }
    }
  }
  return {
    category: null,
    tokenName: key
  };
}
function extractValue(value) {
  if (typeof value === 'string') {
    return value;
  }
  // TODO: theme fields pass through a single number as the value without the unit "px"
  // We need to figure out support for applying the px value to the theme which is what
  // trellis expects. Need to confirm where we set numbers and units to confirm approach.
  if (typeof value === 'number') {
    return String(value);
  }

  // TODO: likely will need more handling for various content theme fields
  if (value && typeof value === 'object') {
    // Handle ColorValue - check for properties unique to ColorValue
    if ('hex' in value || 'rgba' in value || 'rgb' in value || 'opacity' in value) {
      return value.hex || value.rgba || value.color || undefined;
    }
    // Handle FontFieldValue
    if ('css' in value && value.css) {
      return value.css;
    }
  }
  return undefined;
}

// Map brand settings to theme overrides using color variant derivation
function mapBrandSettingsToTheme(brandSettings) {
  const colorOverrides = {};
  const fontOverrides = {};

  // Derive colors for each configured color token
  Object.values(COLOR_OVERRIDE_CONFIGS).forEach(({
    brandField,
    deriveVariants
  }) => {
    if (brandField === null) return;
    const baseColor = extractValue(brandSettings[brandField]);
    if (baseColor) {
      Object.assign(colorOverrides, deriveVariants(baseColor));
    }
  });
  Object.values(FONT_OVERRIDE_CONFIGS).forEach(({
    brandField,
    deriveVariants
  }) => {
    const fontValue = brandSettings[brandField];
    if (isFontValue(fontValue)) {
      Object.assign(fontOverrides, deriveVariants(buildFontFamilyString(fontValue.font)));
    }
  });
  const result = {};
  if (Object.keys(colorOverrides).length > 0) {
    result.color = colorOverrides;
  }
  if (Object.keys(fontOverrides).length > 0) {
    result.typography = fontOverrides;
  }
  return result;
}

/**
 * Map theme settings to theme overrides
 * Handles trellis-prefixed keys and special cases like fontFamily
 * Derives all related tokens (fill variants, borders, text) if only default is provided
 * Optionally applies custom theme settings mappings with variant derivation
 */
export function mapThemeSettingsToTheme(themeSettings, tokenOverrides) {
  const result = {};
  // Replace pre-defined token values with values from theme settings
  Object.keys(themeSettings).forEach(key => {
    if (key === 'trellisColorFillSurfaceDefault') return;
    const value = themeSettings[key];
    if (!value) return;
    const {
      category,
      tokenName
    } = parseThemeSettingKey(key);
    if (!category) return;
    const extractedValue = extractValue(value);
    if (!extractedValue) return;
    if (!result[category]) {
      result[category] = {};
    }
    result[category][tokenName] = extractedValue;
  });

  // Derive variants for color tokens
  if (!result.color) {
    result.color = {};
  }
  const colorMap = result.color;
  Object.values(COLOR_OVERRIDE_CONFIGS).forEach(({
    themeSettingField,
    deriveVariants
  }) => {
    if (themeSettingField === null) return;
    const themeValue = themeSettings[themeSettingField];
    const baseColor = extractValue(themeValue);
    if (baseColor) {
      Object.assign(colorMap, deriveVariants(baseColor));
    }
  });

  // Derive surface color variants (uses 'light' | 'dark' style instead of hex)
  const surfaceStyle = themeSettings[SURFACE_STYLE_OVERRIDE_CONFIG.themeSettingField];
  if (surfaceStyle) {
    Object.assign(colorMap, SURFACE_STYLE_OVERRIDE_CONFIG.deriveVariants(surfaceStyle));
  }

  // Derive variants for border radius tokens
  if (!result.borderRadius) {
    result.borderRadius = {};
  }
  const borderRadiusMap = result.borderRadius;
  BORDER_RADIUS_OVERRIDE_CONFIGS.forEach(({
    themeSettingField,
    deriveVariants
  }) => {
    const themeValue = themeSettings[themeSettingField];
    const baseChoice = extractValue(themeValue);
    if (baseChoice) {
      const variants = deriveVariants(baseChoice);
      if (variants) {
        Object.assign(borderRadiusMap, variants);
      }
    }
  });
  Object.values(FONT_OVERRIDE_CONFIGS).forEach(({
    themeSettingField,
    deriveVariants
  }) => {
    const fontValue = themeSettings[themeSettingField];
    if (isFontFieldValue(fontValue)) {
      const fontFamily = resolveThemeFontFamily(fontValue);
      if (!fontFamily) return;
      if (!result.typography) {
        result.typography = {};
      }
      Object.assign(result.typography, deriveVariants(fontFamily));
    }
  });
  if (tokenOverrides) {
    for (const [cmsField, targetToken] of Object.entries(tokenOverrides)) {
      if (!(cmsField in themeSettings)) {
        console.warn(`Token mapping references unknown field: "${cmsField}". Check that this field exists within your theme.`);
        continue;
      }
      const fieldValue = themeSettings[cmsField];
      const extractedColor = extractValue(fieldValue);
      if (extractedColor) {
        const variants = deriveGenericColorVariants(extractedColor, targetToken);
        Object.assign(colorMap, variants);
      }
    }
  }
  return result;
}

// This is not being used right now but I think we will keep around
// until final patterns are solidified. I think we need something like this
// if the portal is set to canvas and not trellis and we'd need to override the canvas theme.
// But I need to verify this in practice before we decide to keep or remove it.
// function applyOverridesToAllVariants(
//   baseTheme: Record<string, Theme>,
//   overrides: DeepPartial<Theme>
// ): Record<string, Theme> {
//   const result: Record<string, Theme> = {};
//   const themeVariants = [
//     'canvas-light',
//     'trellis-light',
//     'trellis-dark',
//   ] as const;

//   themeVariants.forEach((variant) => {
//     if (baseTheme[variant]) {
//       result[variant] = deepMerge(baseTheme[variant], overrides);
//     }
//   });

//   return result;
// }

/**
 * Merges theme overrides in priority order:
 * 1. Default overrides (lowest priority)
 * 2. Brand settings (medium priority)
 * 3. Theme settings (highest priority)
 * 4. Custom theme settings (highest priority - applied within theme settings)
 */
export function buildThemeOverrides(brandAndThemeData, tokenOverrides) {
  var _brandAndThemeData$co, _brandAndThemeData$co2, _brandAndThemeData$co3, _brandAndThemeData$co4;
  const surfaceStyle = (_brandAndThemeData$co = brandAndThemeData === null || brandAndThemeData === void 0 || (_brandAndThemeData$co2 = brandAndThemeData.context) === null || _brandAndThemeData$co2 === void 0 || (_brandAndThemeData$co2 = _brandAndThemeData$co2.themeSettings) === null || _brandAndThemeData$co2 === void 0 ? void 0 : _brandAndThemeData$co2.trellisColorFillSurfaceDefault) !== null && _brandAndThemeData$co !== void 0 ? _brandAndThemeData$co : 'light';
  let result = applyDefaultOverrides(surfaceStyle);

  // Apply brand settings overrides
  if (brandAndThemeData !== null && brandAndThemeData !== void 0 && (_brandAndThemeData$co3 = brandAndThemeData.context) !== null && _brandAndThemeData$co3 !== void 0 && _brandAndThemeData$co3.brandSettings) {
    const brandOverrides = mapBrandSettingsToTheme(brandAndThemeData.context.brandSettings);
    result = deepMerge(result, brandOverrides);
  }

  // Apply theme settings overrides (highest priority)
  if (brandAndThemeData !== null && brandAndThemeData !== void 0 && (_brandAndThemeData$co4 = brandAndThemeData.context) !== null && _brandAndThemeData$co4 !== void 0 && _brandAndThemeData$co4.themeSettings) {
    const themeOverrides = mapThemeSettingsToTheme(brandAndThemeData.context.themeSettings, tokenOverrides);
    result = deepMerge(result, themeOverrides);
  }

  // Applied after every source has been merged so the floor keys off the font
  // each token actually ends up with. Brand settings may name a known thin font
  // that theme settings then override with a different family, in which case no
  // token should be floored.
  if (result.typography) {
    applyThinFontWeightFloor(result.typography);
  }
  return result;
}
function loadPreviewListener(themeMode, initialOverrides) {
  import('./preview').then(({
    initializePreviewListener
  }) => {
    initializePreviewListener(themeMode, initialOverrides);
  }).catch(error => {
    console.error('Failed to initialize preview listener:', error);
  });
}

// Apply the portal's merged Trellis theme to the global theme manager. The
// `themeMode` should match the brand's surface setting so that any tokens not
// covered by the explicit overrides resolve against the matching base theme.
export function setPortalTheme(themeOverrides, themeMode) {
  if (themeOverrides.typography) {
    const baseTheme = globalThemeManager.getTheme();
    themeOverrides = Object.assign({}, themeOverrides, {
      typography: deepMerge(baseTheme.typography, themeOverrides.typography)
    });
  }
  globalThemeManager.setTheme({
    themeOverrides,
    themeName: 'trellis',
    themeMode
  });
}

// Our portal surface overrides target different subsets of tokens per mode —
// each mode only overrides tokens where the trellis base is wrong for portals.
// When switching modes, stale overrides from the old mode must be stripped so
// the new base's values come through for tokens the new mode doesn't override.
export function stripSurfaceColorOverrides(overrides) {
  if (!overrides.color) return overrides;
  const lightSurfaceOverrideKeys = Object.keys(deriveSurfaceTokensFromStyle('light'));
  const darkSurfaceOverrideKeys = Object.keys(deriveSurfaceTokensFromStyle('dark'));
  const allSurfaceOverrideKeys = new Set([...lightSurfaceOverrideKeys, ...darkSurfaceOverrideKeys]);
  const cleanColor = Object.assign({}, overrides.color);
  for (const tokenKey of allSurfaceOverrideKeys) {
    delete cleanColor[tokenKey];
  }
  return Object.assign({}, overrides, {
    color: cleanColor
  });
}
const SCRIPT_LOADER_ID = 'hs-script-loader';
function injectScriptLoader(footerElements = []) {
  var _scriptLoader$attribu;
  if (document.body.querySelector(`script[id="${SCRIPT_LOADER_ID}"]`)) return;
  const scriptLoader = footerElements.find(el => el.id === SCRIPT_LOADER_ID);
  if (!(scriptLoader !== null && scriptLoader !== void 0 && (_scriptLoader$attribu = scriptLoader.attributes) !== null && _scriptLoader$attribu !== void 0 && _scriptLoader$attribu.src)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_LOADER_ID;
  Object.entries(scriptLoader.attributes).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });
  document.body.appendChild(script);
}
function getInlineBrandData() {
  const script = document.querySelector('#hs-portals-brand-data');
  if (!(script !== null && script !== void 0 && script.textContent)) {
    return undefined;
  }
  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.error('Failed to parse inline brand data', e);
    return undefined;
  }
}
/**
 * Fetches brand and theme data
 * Creates a modified theme by applying that data to the base trellis theme
 * Enforces the use of the `trellis-light` theme object
 * Automatically applies the theme to the global theme manager
 *
 * @param config - Optional configuration object
 * @param config.tokenOverrides - Mapping of CMS theme fields to Trellis tokens with variant derivation
 * @returns Useful portal data and applies the modified theme object to the global theme manager
 *
 */
export async function initializePortal(config) {
  var _loginVerifyResult$da, _loginVerifyData$feat, _loginVerifyData$feat2, _portalData$context, _getPortalName;
  const {
    client,
    tokenOverrides,
    objectId
  } = config;
  const params = new URLSearchParams(window.location.search);
  const isThemePreviewer = getIsThemePreview();

  // Used to test the default colors for a portal instead of the user defined colors
  const useDefaults = params.get('useDefaults') === 'true';
  const loginVerifyResult = await client.query({
    query: LOGIN_VERIFY_QUERY,
    variables: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
      objectId,
      tolerateInstanceAccessDenied: true
    }
  }).catch(e => {
    console.error('Failed to resolve loginVerify', e);
    return undefined;
  });
  const loginVerifyData = loginVerifyResult === null || loginVerifyResult === void 0 || (_loginVerifyResult$da = loginVerifyResult.data) === null || _loginVerifyResult$da === void 0 ? void 0 : _loginVerifyResult$da.result;
  const isInlineBrandDataEnabled = (_loginVerifyData$feat = loginVerifyData === null || loginVerifyData === void 0 || (_loginVerifyData$feat2 = loginVerifyData.featuresEnabled) === null || _loginVerifyData$feat2 === void 0 ? void 0 : _loginVerifyData$feat2.includes('PortalsPlatform::InlineBrandData')) !== null && _loginVerifyData$feat !== void 0 ? _loginVerifyData$feat : false;
  if (!useDefaults && !isThemePreviewer && isInlineBrandDataEnabled) {
    const inlineData = getInlineBrandData();
    if (inlineData) {
      client.cache.writeQuery({
        query: FETCH_BRAND_DATA,
        data: {
          brandData: inlineData
        }
      });
    }
  }
  const portalData = useDefaults ? {
    context: {}
  } : await client.query({
    query: FETCH_BRAND_DATA
  }).then(result => result.data.brandData).catch(e => {
    console.error('Error fetching brand and theme data', e);
    return {
      context: {}
    };
  });
  trackPortalInitialized({
    email: loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.email,
    isOwner: (loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.userId) != null
  });

  // Derive the theme mode from the brand's surface setting so foundations-theming
  // loads the matching dark/light base theme.
  const surfaceMode = portalData === null || portalData === void 0 || (_portalData$context = portalData.context) === null || _portalData$context === void 0 || (_portalData$context = _portalData$context.themeSettings) === null || _portalData$context === void 0 ? void 0 : _portalData$context.trellisColorFillSurfaceDefault;
  const themeMode = surfaceMode === 'dark' ? 'dark' : 'light';
  const themeOverrides = buildThemeOverrides(portalData, tokenOverrides);

  // Initialize preview listener for hot reload in the theme previewer
  if (isThemePreviewer) {
    loadPreviewListener(themeMode, themeOverrides);
  }
  setPortalTheme(themeOverrides, themeMode);
  injectScriptLoader(portalData.footerElements);
  const baseTheme = globalThemeManager.getTheme();
  const portalTheme = deepMerge(baseTheme, themeOverrides);
  Metrics.timer('portal-initialized', {
    portal_name: (_getPortalName = getPortalName()) !== null && _getPortalName !== void 0 ? _getPortalName : 'unknown',
    inline_brand_data: String(isInlineBrandDataEnabled)
  }).update(performance.now());
  return {
    portalTheme,
    portalData
  };
}